import { Button } from '@components/button/button'
import { Text } from '@components/text'
import { renderTemplate, TEXT } from '@lib/utils/text'
import type { FunctionComponent, JSX } from 'react'
import { useState } from 'react'

import styles from './amount-quick-select.module.scss'

const QUICK_BUTTON_OPTIONS: number[] = [20, 50, 100]

export const AmountQuickSelect: FunctionComponent<Props> = ({
    amount,
    amountInput,
    options = QUICK_BUTTON_OPTIONS,
    recommendedAmount,
    showOtherOption = false,
    staySelectedOnClick = false,
    onSelect,
}) => {
    const [showOtherInput, setShowOtherInput] = useState(
        showOtherOption && !options.includes(amount ?? 0) && !!amount,
    )
    const [showRecommendedAmount, setShowRecommendedAmount] = useState(
        !!recommendedAmount && !amount,
    )

    return (
        <div className={styles['wrapper']}>
            <div className={styles['button-row']}>
                {options.map((value) => {
                    const buttonPreset =
                        amount === value &&
                        !showOtherInput &&
                        staySelectedOnClick
                            ? 'primary'
                            : 'secondary'

                    return (
                        <div key={value} className={styles['button-column']}>
                            <Button
                                type='button'
                                preset={buttonPreset}
                                size='medium'
                                onClick={() => {
                                    onSelect(value)
                                    setShowOtherInput(false)
                                    setShowRecommendedAmount(false)
                                }}
                                analyticsProperties={{ amount: value }}
                                className={styles['button']}
                                animatePress={false}
                            >
                                ${value}
                            </Button>
                        </div>
                    )
                })}
                {showOtherOption && amountInput && (
                    <div className={styles['button-column']}>
                        <Button
                            type='button'
                            preset={showOtherInput ? 'primary' : 'secondary'}
                            size='medium'
                            onClick={() => {
                                setShowOtherInput(true)
                                setShowRecommendedAmount(false)
                            }}
                            analyticsProperties={{ amount: 'other' }}
                            className={styles['button']}
                            animatePress={false}
                        >
                            {TEXT.other}
                        </Button>
                    </div>
                )}
            </div>

            {showOtherInput && (
                <div className={styles['other-input']}>{amountInput}</div>
            )}

            {showRecommendedAmount && (
                <Text
                    tag='div'
                    className={styles['recommended-amount']}
                    typeStyle='body-2xs'
                    color='springtime'
                >
                    {renderTemplate(TEXT.mostParentsChooseAmount, {
                        amount: recommendedAmount,
                    })}
                </Text>
            )}
        </div>
    )
}

interface Props {
    amount?: number
    amountInput?: JSX.Element
    options?: number[]
    recommendedAmount?: number
    showOtherOption?: boolean
    staySelectedOnClick?: boolean
    onSelect: (amount: number) => void
}
